import { useState } from "react";
import { Input } from "shared/ui/Input";
import { PasswordInput } from "shared/ui/Input";
import { Button } from "shared/ui/button";
import { useHttpRequest } from "shared/lib/hooks/useHttpRequest";
import { registrationRequest } from "features/login/api/registrationRequest";
import {
  validateBusinessForm,
  businessValidateOnBlur,
  businessClearOnChange,
  prepareBusinessFormData,
} from "../../lib/register-validation/businessValidation";
import { useFormValidation } from "../../lib/register-validation/common";
import { SopdAgreement } from "../agreement/ui/SopdAgreement";
import styles from "../FormStyles.module.scss";

export const RegistrationBusinessForm = ({ onSuccess }) => {
  const [agreement, setAgreement] = useState({
    checked: false,
    checkedAt: "",
  });

  const [serverError, setServerError] = useState("");

  const handleAgreementChange = ({ checked, checkedAt }) => {
    setAgreement({ checked, checkedAt });
  };

  const { sendRequest, loading, error, clearError } =
    useHttpRequest(registrationRequest);

  const {
    formData,
    fieldErrors,
    errorMessages,
    requiredFieldsError,
    handleInputChange,
    handleBlur,
    validateForm,
  } = useFormValidation(
    {
      companyName: "",
      inn: "",
      kpp: "",
      phoneNumber: "",
      email: "",
      fio: "",
      password: "",
      confirmedPassword: "",
    },
    {
      validate: validateBusinessForm,
      validateOnBlur: businessValidateOnBlur,
      clearOnChange: businessClearOnChange,
    },
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { isValid, requiredFieldsError: requiredError } = validateForm();

    if (!isValid || loading) return;

    setServerError("");

    const submitData = prepareBusinessFormData(formData, agreement);

    try {
      await sendRequest(submitData);
      onSuccess?.(formData.email);
    } catch (error) {
      const status = error?.status;

      if (status === 401) {
        return;
      }

      if (error?.status === 409) {
        setServerError(
          error?.data?.detail ||
            error?.data?.message ||
            error?.message ||
            "Произошла ошибка, попробуйте позже",
        );
        return;
      }

      if (status >= 500) {
        return;
      }

      setServerError("Произошла ошибка, попробуйте позже");
    }
  };

  return (
    <form className={styles.formWrapper}>
      <div className={styles.formWrapper__form}>
        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>Наименование</p>
          <Input
            type="text"
            value={formData.companyName}
            onChange={(e) => handleInputChange("companyName", e.target.value)}
            onBlur={(e) => handleBlur("companyName", e.target.value)}
            placeholder="Введите наименование"
            className={`${fieldErrors.companyName ? styles.formWrapper__inputError : styles.formWrapper__input}`}
          />
        </div>

        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>ИНН</p>
          <Input
            type="text"
            value={formData.inn}
            onChange={(e) => handleInputChange("inn", e.target.value)}
            onBlur={(e) => handleBlur("inn", e.target.value)}
            placeholder="Введите номер ИНН"
            className={`${fieldErrors.inn ? styles.formWrapper__inputError : styles.formWrapper__input}`}
            mask="____________"
            replacement={{ _: /\d/ }}
          />
        </div>

        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>КПП</p>
          <Input
            type="text"
            value={formData.kpp}
            onChange={(e) => handleInputChange("kpp", e.target.value)}
            onBlur={(e) => handleBlur("kpp", e.target.value)}
            placeholder="Введите номер КПП"
            className={`${fieldErrors.kpp ? styles.formWrapper__inputError : styles.formWrapper__input}`}
            mask="_________"
            replacement={{ _: /\d/ }}
          />
        </div>

        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>Контактный номер телефона</p>
          <Input
            type="text"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
            onBlur={(e) => handleBlur("phoneNumber", e.target.value)}
            placeholder="+7"
            className={`${fieldErrors.phoneNumber ? styles.formWrapper__inputError : styles.formWrapper__input}`}
            mask="+7 (___) ___-__-__"
            replacement={{ _: /\d/ }}
          />
        </div>

        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>Электронная почта</p>
          <Input
            type="text"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onBlur={(e) => handleBlur("email", e.target.value)}
            placeholder="example@mail.ru"
            autoComplete="username"
            className={`${fieldErrors.email ? styles.formWrapper__inputError : styles.formWrapper__input}`}
          />
          {errorMessages.email && (
            <span className={styles.formWrapper__errorText}>
              {errorMessages.email}
            </span>
          )}
        </div>

        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>ФИО контактного лица</p>
          <Input
            type="text"
            value={formData.fio}
            onChange={(e) => handleInputChange("fio", e.target.value)}
            onBlur={(e) => handleBlur("fio", e.target.value)}
            placeholder="Введите ФИО"
            autoComplete="name"
            className={`${fieldErrors.fio ? styles.formWrapper__inputError : styles.formWrapper__input}`}
          />
          {errorMessages.fio && (
            <span className={styles.formWrapper__errorText}>
              {errorMessages.fio}
            </span>
          )}
        </div>

        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>Пароль</p>
          <PasswordInput
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            onBlur={(e) => handleBlur("password", e.target.value)}
            placeholder="Введите пароль"
            autoComplete="new-password"
            className={`${fieldErrors.password ? styles.formWrapper__inputError : styles.formWrapper__input} ${styles.formWrapper__passwordBtn}`}
          />
          {errorMessages.password && (
            <span className={styles.formWrapper__errorText}>
              {errorMessages.password}
            </span>
          )}
        </div>

        <div className={styles.formWrapper__formItem}>
          <p className={styles.formWrapper__label}>Повторите пароль</p>
          <PasswordInput
            value={formData.confirmedPassword}
            onChange={(e) =>
              handleInputChange("confirmedPassword", e.target.value)
            }
            onBlur={(e) => handleBlur("confirmedPassword", e.target.value)}
            placeholder="Введите пароль"
            autoComplete="new-password"
            className={`${fieldErrors.confirmedPassword ? styles.formWrapper__inputError : styles.formWrapper__input} ${styles.formWrapper__passwordBtn}`}
          />
          {errorMessages.confirmedPassword && (
            <span className={styles.formWrapper__errorText}>
              {errorMessages.confirmedPassword}
            </span>
          )}
        </div>

        {requiredFieldsError && (
          <span className={styles.formWrapper__greenText}>
            {requiredFieldsError}
          </span>
        )}

        {serverError && (
          <span className={styles.formWrapper__greenText}>{serverError}</span>
        )}

        <div>
          <SopdAgreement
            checked={agreement.checked}
            onChange={handleAgreementChange}
          />
        </div>
      </div>

      <div className={styles.formWrapper__buttonBlock}>
        <div className={styles.formWrapper__button}>
          <Button
            variant={agreement.checked ? "primary" : "disabled"}
            size="large"
            onClick={handleSubmit}
            type="submit"
            disabled={!agreement.checked}
          >
            Зарегистрироваться
          </Button>
        </div>
      </div>
    </form>
  );
};
